/**
 * @fileoverview Componente principal de la aplicación que maneja la configuración inicial
 * y la estructura base de la interfaz.
 */
import { Component, effect, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PrimeNG } from 'primeng/config';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { ConfirmationService } from 'primeng/api';

import { ThemeService } from '../protected-area/services/theme.service';
import { setupPrimeNGConfig } from './primeng-config';
import { AppVersionService } from './services/app-version.service';

/**
 * @class AppComponent
 * @description Componente raíz de la aplicación que inicializa las configuraciones básicas
 * y gestiona el estado global de la aplicación.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, Toast, ConfirmDialog],
  template: ` <!-- Toast para mostrar mensajes -->
    <p-toast [position]="'top-right'" [preventOpenDuplicates]="true"></p-toast>

    <!-- Dialogod de confirmación -->
    <p-confirmdialog
      icon="pi pi-exclamation-triangle"
      acceptButtonStyleClass="p-button-primary"
      acceptLabel="Aceptar"
      acceptIcon="pi pi-check"
      rejectButtonStyleClass="p-button-secondary p-button-outlined"
      rejectLabel="Cancelar"
    />

    <!-- Contenido de la aplicación -->
    <router-outlet />`,
})
export class AppComponent implements OnInit {
  private readonly primengConfig = inject(PrimeNG);
  private readonly themeService = inject(ThemeService);
  private readonly msalService = inject(MsalService);
  private readonly versionService = inject(AppVersionService);
  private readonly confirmationService = inject(ConfirmationService);

  /** Versión por la que ya se abrió el diálogo, para no reabrirlo. */
  private promptedVersion: string | null = null;

  private readonly _updateEffect = effect(() => {
    const update = this.versionService.updateAvailable();
    if (!update) return;
    if (this.promptedVersion === update.version) return;

    this.promptedVersion = update.version;

    this.confirmationService.confirm({
      header: 'Actualización disponible',
      message:
        'Hay una nueva versión disponible. ¿Deseas recargar la aplicación?',
      acceptLabel: 'Recargar',
      rejectLabel: 'Más tarde',
      accept: () => window.location.reload(),
      reject: () => this.versionService.clearUpdateAvailable(),
    });
  });

  /**
   * @constructor
   */
  constructor() {
    // Initialize MSAL at app startup
    this.msalService.initialize().subscribe();
  }

  ngOnInit() {
    setupPrimeNGConfig(this.primengConfig);
    this.themeService.initializeTheme();
    this.versionService.startPolling();
  }
}
